import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X, Phone, MessageCircle, Mail } from "lucide-react";
import TopBar from "./TopBar";
import Logo from "./Logo";
import MegaMenu from "./MegaMenu";
import { products, company } from "../mock/mock";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-200">
      <TopBar />
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          <NavLink to="/" end className={({ isActive }) => `nav-link text-[15px] font-medium ${isActive ? "text-[#c8102e] active" : "text-[#0d2240] hover:text-[#c8102e]"}`}>
            Home
          </NavLink>
          <NavLink to="/about-us" className={({ isActive }) => `nav-link text-[15px] font-medium ${isActive ? "text-[#c8102e] active" : "text-[#0d2240] hover:text-[#c8102e]"}`}>
            About Us
          </NavLink>
          <div className="relative group">
            <button
              onClick={() => navigate("/products")}
              className="nav-link inline-flex items-center gap-1 text-[15px] font-medium text-[#0d2240] hover:text-[#c8102e]"
            >
              Products <ChevronDown size={15} />
            </button>
            <MegaMenu products={products} />
          </div>
          <NavLink to="/contact-us" className={({ isActive }) => `nav-link text-[15px] font-medium ${isActive ? "text-[#c8102e] active" : "text-[#0d2240] hover:text-[#c8102e]"}`}>
            Contact Us
          </NavLink>
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <Link
            to="/contact-us"
            className="px-4 py-2 rounded-md bg-[#c8102e] hover:bg-[#9a0c24] text-white text-sm font-semibold tracking-wide transition-colors shadow-sm"
          >
            Get a Quote
          </Link>
        </div>
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden w-10 h-10 grid place-items-center rounded-md hover:bg-gray-100"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white">
          <div className="px-4 py-3 flex flex-col gap-1">
            <MobileLink to="/" onClick={() => setMobileOpen(false)}>Home</MobileLink>
            <MobileLink to="/about-us" onClick={() => setMobileOpen(false)}>About Us</MobileLink>
            <button
              onClick={() => setMobileProductsOpen((v) => !v)}
              className="flex items-center justify-between py-3 border-b border-gray-100 text-[#0d2240] font-medium"
            >
              <span>Products</span>
              <ChevronDown size={16} className={`transition-transform ${mobileProductsOpen ? "rotate-180" : ""}`} />
            </button>
            {mobileProductsOpen && (
              <div className="pl-3 pb-2">
                {products.map((p) => (
                  <details key={p.id} className="py-1">
                    <summary className="py-2 text-[14px] text-[#0d2240] hover:text-[#c8102e] cursor-pointer list-none flex items-center justify-between">
                      <span>{p.name}</span>
                      <ChevronDown size={14} />
                    </summary>
                    <div className="pl-3 flex flex-col">
                      <Link
                        to={`/products/${p.slug}`}
                        onClick={() => setMobileOpen(false)}
                        className="py-1.5 text-[13px] text-gray-600 hover:text-[#c8102e]"
                      >
                        » View All {p.name}
                      </Link>
                      {p.subProducts.map((s) => (
                        <Link
                          key={s.id}
                          to={`/products/${p.slug}/${s.slug}`}
                          onClick={() => setMobileOpen(false)}
                          className="py-1.5 text-[13px] text-gray-600 hover:text-[#c8102e]"
                        >
                          • {s.name}
                        </Link>
                      ))}
                    </div>
                  </details>
                ))}
              </div>
            )}
            <MobileLink to="/contact-us" onClick={() => setMobileOpen(false)}>Contact Us</MobileLink>
            <div className="pt-3 grid grid-cols-3 gap-2">
              <a href={`tel:+91${company.phone}`} className="flex items-center justify-center gap-1.5 py-2 rounded-md bg-[#c8102e] text-white text-xs font-semibold">
                <Phone size={13} /> Call
              </a>
              <a href={`https://wa.me/91${company.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 py-2 rounded-md bg-[#25D366] text-white text-xs font-semibold">
                <MessageCircle size={13} /> WhatsApp
              </a>
              <a href={`mailto:${company.email}`} className="flex items-center justify-center gap-1.5 py-2 rounded-md bg-[#0d2240] text-white text-xs font-semibold">
                <Mail size={13} /> Email
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      onClick={onClick}
      className={({ isActive }) => `py-3 border-b border-gray-100 font-medium ${isActive ? "text-[#c8102e]" : "text-[#0d2240]"}`}
    >
      {children}
    </NavLink>
  );
}
